import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const PurchaseEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/purchases",
    token
  );

  const { data: suppliersData, loading: supplierLoading } = useFetchObjects(
    ["supplier"],
    "shop/suppliers?page_size=all",
    token
  );
  const { data: productsData, loading: productLoading } = useFetchObjects(
    ["product"],
    "shop/products?page_size=all",
    token
  );
  const { data: unitsData, loading: unitLoading } = useFetchObjects(
    ["unit"],
    "shop/units?page_size=all",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies?page_size=all",
    token
  );

  const suppliers = Array.isArray(suppliersData) ? suppliersData : suppliersData?.results || [];
  const products = Array.isArray(productsData) ? productsData : productsData?.results || [];
  const units = Array.isArray(unitsData) ? unitsData : unitsData?.results || [];
  const currencies = getUniqueCurrencies(
    Array.isArray(currenciesData) ? currenciesData : currenciesData?.results || []
  );

  const [shop, setShop] = useState("");
  const [supplier, setSupplier] = useState("");
  const [discount, setDiscount] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [purchaseCurrency, setPurchaseCurrency] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [items, setItems] = useState([]);
  const [formError, setFormError] = useState("");

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "purchase-edit", shop],
    shop ? `shop/accounts/?page_size=all&shop=${shop}` : "",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];
  const selectedAccount = useMemo(
    () => accounts.find((acc) => String(acc.id) === String(selectedAccountId)) || null,
    [accounts, selectedAccountId]
  );
  const filteredSuppliers = suppliers;
  const selectedSupplierOption =
    filteredSuppliers.find((sup) => String(sup.id) === String(supplier)) || null;
  const sameCurrencyAccounts = useMemo(
    () => accounts.filter((acc) => String(acc.currency) === String(purchaseCurrency)),
    [accounts, purchaseCurrency]
  );

  const { data: directRateData } = useFetchObjects(
    ["purchase-edit-rate-direct", shop, purchaseCurrency, selectedAccount?.currency],
    shop && purchaseCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${shop}&from_currency=${purchaseCurrency}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    ["purchase-edit-rate-reverse", shop, purchaseCurrency, selectedAccount?.currency],
    shop && purchaseCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${shop}&from_currency=${selectedAccount.currency}&to_currency=${purchaseCurrency}`
      : "",
    token
  );

  useEffect(() => {
    if (editData) {
      setShop(editData.shop ? String(editData.shop) : "");
      setSupplier(editData.supplier ? String(editData.supplier) : "");
      setDiscount(editData.amount || "");
      
      setReceivedAmount(editData.received_amount || "");
      setPurchaseCurrency(editData.currency ? String(editData.currency) : "");
      setPurchaseDate(
        editData.purchase_date ? editData.purchase_date.substring(0, 16) : ""
      );
      if (editData.purchase_items) {
        const initialItems = editData.purchase_items.map((item) => ({
          id: item.id,
          product: item.product ? String(item.product) : "",
          unit: item.unit ? String(item.unit) : "",
          quantity: item.quantity,
          buy_price: item.buy_price,
          wholesale_price: item.wholesale_price || "",
          retail_price: item.retail_price || item.sale_price || "",
        }));
        setItems(initialItems);
      }
      const latestPayment = [...(editData.payments || [])].sort((a, b) => b.id - a.id)[0];
      setSelectedAccountId(latestPayment?.account ? String(latestPayment.account) : "");
      setExchangeRate(latestPayment?.exchange_rate || "");
      setPaymentNotes(latestPayment?.notes || "");
      setFormError("");
    }
  }, [editData]);

  useEffect(() => {
    if (!shop || !purchaseCurrency || accounts.length === 0) {
      setSelectedAccountId("");
      return;
    }
    const exists = accounts.some((acc) => String(acc.id) === String(selectedAccountId));
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || accounts[0];
      setSelectedAccountId(preferred ? String(preferred.id) : "");
    }
  }, [shop, purchaseCurrency, accounts, sameCurrencyAccounts, selectedAccountId]);

  const addItem = () => {
    setItems([
      ...items,
      {
        product: "",
        unit: "",
        quantity: "",
        buy_price: "",
        wholesale_price: "",
        retail_price: "",
      },
    ]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const getItemTotal = (item) => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.buy_price) || 0;
    return qty * price;
  };

  const totalAmount = items.reduce((acc, item) => {
    return acc + getItemTotal(item);
  }, 0);

  const discountValue = parseFloat(discount) || 0;
  const finalTotal = totalAmount - discountValue;

  const itemsValid =
    items.length > 0 &&
    items.every(
      (item) =>
        item.product &&
        item.unit &&
        item.quantity &&
        item.buy_price &&
        item.retail_price
    );
  const parsedReceived = parseFloat(receivedAmount) || 0;
  const receivedValid = parsedReceived <= finalTotal;
  const needsPaymentAccount = parsedReceived > 0;

  const exchangeRatePreview = useMemo(() => {
    if (!purchaseCurrency) return "";
    const accountCurrencyId = selectedAccount?.currency ?? purchaseCurrency;
    if (String(purchaseCurrency) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [purchaseCurrency, selectedAccount, directRateData, reverseRateData]);

  useEffect(() => {
    if (exchangeRatePreview) {
      setExchangeRate(exchangeRatePreview);
    }
  }, [exchangeRatePreview]);

  const convertedAmount = useMemo(() => {
    const nAmount = Number(receivedAmount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [receivedAmount, exchangeRate]);

  const getProductOptionLabel = (product) => {
    if (!product) return "";
    return product.name || "";
  };

  const getSupplierOptionLabel = (sup) => {
    if (!sup) return "";
    const name = sup.name || "";
    const contact = sup.contact ? ` • ${sup.contact}` : "";
    return `${name}${contact}`;
  };

  const handleSubmit = () => {
    if (!shop || !purchaseCurrency || !itemsValid) {
      setFormError(
        t("purchase.completeItemFields") ||
          "Please complete all required purchase fields"
      );
      return;
    }
    if (!receivedValid) {
      setFormError(
        t("purchase.receivedExceedsTotal") ||
          "Received amount should not exceed total amount"
      );
      return;
    }
    if (needsPaymentAccount && (!selectedAccountId || !exchangeRate)) {
      setFormError(
        t("purchase.accountRequired") ||
          "Select an account and exchange rate for the received amount"
      );
      return;
    }
    setFormError("");
    const payload = {
      shop,
      supplier: supplier || null,
      amount: discountValue,
      received_amount: receivedAmount,
      currency: purchaseCurrency,
      purchase_date: purchaseDate,
      account: needsPaymentAccount ? selectedAccountId : null,
      exchange_rate: needsPaymentAccount ? exchangeRate : "",
      payment_date: purchaseDate
        ? `${purchaseDate}T00:00:00`
        : new Date().toISOString(),
      payment_notes: paymentNotes,
      items,
    };
    handleUpdate(editData.id, payload);
  };

  useEffect(() => {
    if (isSuccess) {
      setShop("");
      setSupplier("");
      setDiscount("");
      setReceivedAmount("");
      setPurchaseCurrency("");
      setPurchaseDate("");
      setSelectedAccountId("");
      setExchangeRate("");
      setPaymentNotes("");
      setItems([]);
      setFormError("");
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{t("purchase.editPurchase")}</DialogTitle>
      <DialogContent>
        {formError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {formError}
          </Typography>
        )}
        <Box
          component="form"
          noValidate
          sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Autocomplete
            options={filteredSuppliers}
            value={selectedSupplierOption}
            onChange={(_, value) => setSupplier(value ? String(value.id) : "")}
            getOptionLabel={getSupplierOptionLabel}
            isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
            loading={supplierLoading}
            disabled={supplierLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("purchase.selectSupplier")}
                fullWidth
                placeholder={t("purchase.searchSupplier", "Search supplier")}
              />
            )}
          />
          <TextField
            select
            label={t("purchase.selectCurrency")}
            fullWidth
            required
            value={purchaseCurrency}
            onChange={(e) => setPurchaseCurrency(e.target.value)}
            disabled={currencyLoading}
          >
            {(currencies || []).map((curr) => (
              <MenuItem key={curr.id} value={String(curr.id)}>
                {curr.title}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t("purchase.purchaseDate")}
            type="date"
            fullWidth
            required
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t("purchase.discount")}
            type="number"
            fullWidth
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6">{t("purchase.purchaseItems")}</Typography>
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                  flexWrap: "wrap",
                }}
              >
                <Autocomplete
                  options={products || []}
                  value={
                    (products || []).find(
                      (prod) => String(prod.id) === String(item.product)
                    ) || null
                  }
                  onChange={(_, value) =>
                    handleItemChange(
                      index,
                      "product",
                      value ? String(value.id) : ""
                    )
                  }
                  getOptionLabel={getProductOptionLabel}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id) === String(value?.id)
                  }
                  loading={productLoading}
                  disabled={productLoading}
                  sx={{ flex: "1 1 180px", minWidth: 180 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("purchase.selectProduct")}
                      required
                    />
                  )}
                />
                <TextField
                  select
                  label={t("purchase.selectUnit")}
                  required
                  value={item.unit}
                  onChange={(e) =>
                    handleItemChange(index, "unit", e.target.value)
                  }
                  sx={{ flex: "1 1 180px", minWidth: 180 }}
                  disabled={unitLoading}
                >
                  {(units || []).map((unit) => (
                    <MenuItem key={unit.id} value={String(unit.id)}>
                      {unit.title}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={t("purchase.quantity")}
                  type="number"
                  required
                  value={item.quantity}
                  onChange={(e) =>
                    handleItemChange(index, "quantity", e.target.value)
                  }
                  sx={{ flex: "1 1 140px", minWidth: 140 }}
                />
                <TextField
                  label={t("purchase.buyPrice")}
                  type="number"
                  required
                  value={item.buy_price}
                  onChange={(e) =>
                    handleItemChange(index, "buy_price", e.target.value)
                  }
                  sx={{ flex: "1 1 140px", minWidth: 140 }}
                />
                <TextField
                  label={t("purchase.salePrice", "Sale Price")}
                  type="number"
                  required
                  value={item.retail_price}
                  onChange={(e) =>
                    handleItemChange(index, "retail_price", e.target.value)
                  }
                  sx={{ flex: "1 1 140px", minWidth: 140 }}
                />
                <TextField
                  label={t("purchase.total", "Total")}
                  type="number"
                  value={getItemTotal(item).toFixed(2)}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: "1 1 140px", minWidth: 140 }}
                />
                <IconButton onClick={() => removeItem(index)} color="error">
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addItem}
              sx={{ mt: 1 }}
            >
              {t("purchase.addItem")}
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
              {t("purchase.subtotal")}: {totalAmount.toFixed(2)}
            </Typography>
            {discountValue > 0 && (
              <Typography variant="body1">
                {t("purchase.discountAmount")}: -{discountValue.toFixed(2)}
              </Typography>
            )}
            <Typography variant="h6">
              {t("purchase.totalAmount")}: {finalTotal.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("purchase.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !purchaseCurrency ||
            items.length === 0 ||
            !itemsValid ||
            (needsPaymentAccount && (!selectedAccountId || !exchangeRate)) ||
            !receivedValid
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("purchase.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseEdit;

